import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import InfoCircle from 'wix-ui-icons-common/InfoCircle';
import Experiments from '@wix/wix-experiments';
import * as s from './App.scss';
import { Heading, Tooltip, Page, FontUpgrade } from 'wix-style-react';

import { PushSection } from './sections/push/component';
import { ExpertSection } from './sections/expert/component';
import { MobileSection } from './sections/channel/mobile/component';
import { ChannelSection } from './sections/channel/component';
import { Channel } from './sections/types';
import { HeightAnimationContainer } from './height.animation.container';

import { APP_PAGE, APP_TITLE, FONT_UPGRADE } from '../../consts/data-hooks';
import { getUserSettings, setUserSettings } from '../../apis/settings-server/settings-server';
import * as browserNotificationsClient from '../../apis/browser-notifications/browser-notifications';
import { getUserProStatus } from '../../apis/account-settings/account-settings';
import { parseSectionsFromResponse } from '../../utils/get-settings-parser';
import { ChannelFilter } from '../../consts/channel-filter';
import { InjectedBiLoggerProps, provideLogger } from '../../services/bi-logger/logger-provider';
import { GetSettingsResponseType } from '../../apis/settings-server/types';
import { getRestaurantAppState } from '../../services/business-manager/get-restauranst-app-state';
import { getHasUserInstalledOneapp } from '../../apis/wix-bi-profile-webapp/wix-bi-profile-webapp';

export interface ChangeNotificationsSettingState {
  isExpert: boolean;
  isGloballyMuted: boolean;
  isPushSupported: boolean;
  isMobileInstalled: boolean;
  restaurantsAppState: any;
  channels: Channel[];
  shouldUseMadefor: boolean;
}

interface SettingAppProps {
  experiments?: Experiments;
  msid: string;
  userId: string;
  onLoadingFinished: () => void;
  locale?: string;
}

class App extends React.Component<InjectedTranslateProps & SettingAppProps & InjectedBiLoggerProps, ChangeNotificationsSettingState> {

  readonly state: ChangeNotificationsSettingState = {
    isExpert: false,
    isGloballyMuted: false,
    isPushSupported: false,
    isMobileInstalled: false,
    restaurantsAppState: null,
    channels: [],
    shouldUseMadefor: false,
  };

  async componentDidMount() {
    const { logger, msid, t, experiments, locale, userId } = this.props;

    const [isExpert, settings, restaurantsAppState, isOneappInstalled] = await Promise.all([
      getUserProStatus().catch(() => false),
      getUserSettings(msid, locale).catch(() => ({} as GetSettingsResponseType)),
      getRestaurantAppState().catch(() => null),
      getHasUserInstalledOneapp(userId).catch(() => false)
    ]);

    const newState: ChangeNotificationsSettingState = { ...this.state, isExpert, isMobileInstalled: isOneappInstalled };
    const exps = experiments || new Experiments();
    await exps.load('my-account');
    await exps.ready();
    const shouldShowMobile = exps.enabled('specs.wos.PingNotificationSettingsShowMobile');
    const shouldShowEmail = exps.enabled('specs.wos.PingNotificationSettingsShowEmail');
    const shouldShowSMS = exps.enabled('specs.wos.PingNotificationSettingsShowSMS');
    const shouldUseMadefor = exps.enabled('specs.wos3.PingNotificationSettingsMadeforFont');
    const shouldShowBrowserNotificationSection = this.shouldShowPushSection();

    if (shouldUseMadefor) {
      newState.shouldUseMadefor = shouldUseMadefor;
    }
    if (restaurantsAppState) {
      newState.restaurantsAppState = restaurantsAppState;
    }
    if (settings.subscribedChannels) {
      newState.channels = parseSectionsFromResponse(settings, t, { shouldShowMobile, shouldShowEmail, shouldShowSMS });

      if (!isOneappInstalled) {
        newState.channels = newState.channels.filter(channel => channel.channelKey !== ChannelFilter.MOBILE);
      }
      newState.isGloballyMuted = settings.globalState.mute || false;
      newState.isPushSupported = shouldShowBrowserNotificationSection;
    }

    logger.reportIsOneappInstalled(isOneappInstalled || false);
    this.setState({ ...newState }, this.props.onLoadingFinished);
  }

  shouldShowPushSection() {
    return browserNotificationsClient.isBrowserNotificationsClientAvailable() &&
      browserNotificationsClient.isBrowserNotificationsSupported();
  }

  onChangeExpertNotifications = (isGloballyMuted: boolean) => {
    const { msid } = this.props;
    this.setState({ isGloballyMuted });
    setUserSettings(msid, ChannelFilter.ALL, isGloballyMuted);
  }

  onGroupSettingChanged = (sectionIndx: number) => (subsectionIndx: number, groupIndx: number) => {
    const { channels } = this.state;
    const { msid, logger } = this.props;
    const channel = channels[sectionIndx];
    const subsection = channel.sections[subsectionIndx];
    const group = subsection.groups[groupIndx];
    const isMuted = group.isMuted;
    group.isMuted = !isMuted;
    logger.reportToggleNotificationGroup(isMuted, channel.channelKey.toLocaleLowerCase(), subsection.sectionKey, group.groupKey.toLowerCase());
    setUserSettings(msid, channel.channelKey, !isMuted, group.groupKey);
    this.setState({ channels });
  }

  renderExpertSection(isExpert, isGloballyMuted) {
    if (isExpert) {
      return <ExpertSection
        isGloballyMuted={isGloballyMuted}
        setGloballyMuted={this.onChangeExpertNotifications}/>;
    } else {
      return null;
    }
  }

  renderChannels() {
    const { channels, restaurantsAppState } = this.state;
    return channels.map((sec, sectionIndx) => {
      return <ChannelSection
        key={sec.channelTitle}
        restaurantsAppState={restaurantsAppState}
        channelKey={sec.channelKey}
        channelTitle={sec.channelTitle}
        channelDescription={sec.channelDescription}
        subsections={sec.sections}
        initiallyOpen={channels.length === 1}
        onGroupSettingChanged={this.onGroupSettingChanged(sectionIndx)}/>;
    });
  }

  renderPage() {
    const { t, msid } = this.props;
    const { isExpert, isGloballyMuted, isPushSupported, isMobileInstalled } = this.state;
    const shouldShowNotifications = !isExpert || !isGloballyMuted;

    return (
      <div className={s.pageWrapper}>
        <div className={s['notifications-page']} data-hook={APP_PAGE}>
          <Page.Header
            title={t('app.title')}
            subtitle={
              <div className={s['page-intro']}>
                {t('app.description')}
                <Tooltip content={t('app.description.tooltip')} maxWidth="250px">
                  <div className={s['page-intro-icon']}>
                    <InfoCircle size="22" color="#3899ec"/>
                  </div>
                </Tooltip>
              </div>}
            dataHook={APP_TITLE}/>
          {this.renderExpertSection(isExpert, isGloballyMuted)}
          <HeightAnimationContainer shouldDisplay={shouldShowNotifications}>
            <div>
              {isPushSupported && <PushSection metaSiteId={msid} />}
              <div className={s.channelsHeader}>
                <Heading appearance="H3">{t('channels.title')}</Heading>
              </div>
              {!isMobileInstalled && <MobileSection key="mobile_section"/>}
              {this.renderChannels()}
            </div>
          </HeightAnimationContainer>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.shouldUseMadefor) {
      return (
        <FontUpgrade dataHook={FONT_UPGRADE}>
          {this.renderPage()}
        </FontUpgrade>
      );
    } else {
      return this.renderPage();
    }
  }
}

export default provideLogger<SettingAppProps>(translate()(App));
